import StearsDashboard from "@/public/assets/img/stears-dashboard-v2.svg"
import { useAnalytics } from "@/src/sb/components/Analytics"
import Button from "@/src/sb/components/Button"
import { pages } from "@/src/sb/constants/copy"
import useRequestDemo from "@/src/sb/hooks/useRequestDemo"
import Image from "next/image"
import { useRouter } from "next/router"
import { useEffect } from "react"

export const DEFAULT_TITLE = "Get Full Access to Africa's Private Market Data"
export const DEFAULT_DESCRIPTION =
  "Discover transactions, companies and investors, benchmark deals with Africa-specific comparables and track sectors, countries and macro risk in real time."

type AccessDeniedContentProps = {
  showSignInButton?: boolean
  title?: string
  description?: string
}

export const AccessDeniedContent = ({
  showSignInButton = false,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION
}: AccessDeniedContentProps) => {
  const router = useRouter()
  const analytics = useAnalytics()
  const requestDemo = useRequestDemo()

  const variant = showSignInButton ? "paywall" : "access_denied"
  const intendedDestination = router.query.next as string | undefined

  useEffect(() => {
    analytics.pageView("access_denied", undefined, { variant })
  }, [analytics, variant])

  const handleRequestTrial = () => {
    window.open(requestDemo.url, "_blank")
    requestDemo.trackClickCorporateQuote({
      context: "access_denied_page",
      variant
    })
  }

  const handleSignIn = () => {
    analytics.clickNavItem("sign_in", { context: "access_denied_page" })
    const signInUrl = intendedDestination
      ? `${pages.signin}?next=${encodeURIComponent(intendedDestination)}`
      : pages.signin
    router.push(signInUrl)
  }

  return (
    <section
      className={
        "bg-[#0E0F10] relative h-full grow flex flex-col items-center pt-[85px] tablet:pt-[94px] mobile:pt-[60px] overflow-hidden"
      }
    >
      <div className="space-y-3 text-center flex flex-col items-center font-jakarta mobile:max-w-[355px] tablet:max-w-[546px] max-w-[600px] z-50 mobile:space-y-2">
        <h1 className="text-xl font-bold leading-7 mobile:text-sm mobile:leading-[22px] tracking-[0px]">
          {title}
        </h1>
        <p className="text-sm tracking-[-0.02px] font-jakarta leading-[22px] mobile:leading-4 mobile:text-xs mobile:tracking-[-0.01px]">
          {description}
        </p>
        <div className="flex flex-row gap-x-[10px] justify-center items-center md:pt-3 xl:pt-0">
          <Button
            type="primary"
            onClick={handleRequestTrial}
            extraClassNames="!bg-stears_white !text-stears_white dark:!text-[#000] min-w-[134px] dark:!text-black font-medium !text-xs sm:!text-sm !text-center mobile:!min-w-[103px] !h-[48px] mobile:!h-[32px] !rounded-none"
          >
            Request a Trial
          </Button>

          {showSignInButton && (
            <Button
              type="primary"
              onClick={handleSignIn}
              extraClassNames="!min-w-[83px] dark:!bg-[#4B5563] mobile:!min-w-[58px] !h-[32px] sm:!h-[48px] !rounded-none !text-xs sm:!text-sm font-medium"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>{" "}
      <div className="flex justify-center tablet:px-6 mobile:px-0">
        <Image
          src={StearsDashboard}
          alt="Stears Dashboard Image"
          width={745}
          height={490}
          className="mobile:mt-[40px] mt-[58px] mb-4"
          priority
        />
      </div>
      <div
        className="bg-[linear-gradient(150deg,rgba(0,0,0,0)_33.64%,#050208_67%)] 
        md:bg-[linear-gradient(160deg,rgba(0,0,0,0)_33.64%,#050208_60%)]
 xl:bg-[linear-gradient(180deg,rgba(0,0,0,0)_43.64%,#050208_96.37%)] 
             w-full h-full absolute inset-0"
      />
    </section>
  )
}
